import { useEffect, useRef, useCallback } from 'react';

const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

interface UseInactivityTimerOptions {
  onIdle: () => void;
  enabled: boolean;
}

/**
 * Fires `onIdle` after INACTIVITY_TIMEOUT_MS of no user interaction,
 * or when the browser tab has been hidden for the same duration.
 * The timer resets on any mouse, keyboard, scroll, or touch event.
 * Call `reset()` to manually restart the timer (e.g. after dismissing a modal).
 */
export function useInactivityTimer({ onIdle, enabled }: UseInactivityTimerOptions): { reset: () => void } {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onIdleRef = useRef(onIdle);
  const enabledRef = useRef(enabled);

  // Keep refs current without re-registering listeners
  useEffect(() => { onIdleRef.current = onIdle; }, [onIdle]);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    if (!enabledRef.current) return;
    timerRef.current = setTimeout(() => {
      if (enabledRef.current) {
        onIdleRef.current();
      }
    }, INACTIVITY_TIMEOUT_MS);
  }, [clearTimer]);

  const reset = useCallback(() => {
    startTimer();
  }, [startTimer]);

  useEffect(() => {
    if (!enabled) {
      clearTimer();
      return;
    }

    // Start immediately when enabled
    startTimer();

    const activityEvents: (keyof WindowEventMap)[] = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'wheel',
    ];

    const handleActivity = () => {
      startTimer();
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Handle tab visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden — start the idle timer (if not already running)
        startTimer();
      } else {
        // Tab visible again — reset the timer so it only counts new inactivity
        startTimer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimer();
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, startTimer, clearTimer]);

  return { reset };
}
